import { trackEvent } from '@/lib/analytics'
import type { PropertyFilters } from '@/types/property'
import {
  filterAppliedParams,
  isBudgetDrag,
  isSortPatch,
  listingChangeEvent,
  mapSearchParams,
  resolveSingleOptionId,
  searchPerformedParams,
  shouldAttachListingSearchLog,
  sortAppliedParams,
} from '@/lib/analytics/searchEventParams'
import type { NamedOptionId } from '@/lib/analytics/searchEventParams'

export {
  filterAppliedParams,
  isBudgetDrag,
  isSortPatch,
  listingChangeEvent,
  mapSearchParams,
  resolveSingleOptionId,
  searchPerformedParams,
  shouldAttachListingSearchLog,
  sortAppliedParams,
}

let pendingSearchSource: string | null = null
let listingSearchLogArmed = false

/** Call once when the user submits a search, before navigation. */
export function markSearchPerformed(source: string): void {
  pendingSearchSource = source
  listingSearchLogArmed = true
}

/**
 * True once for the page-1 listing request that follows a user search.
 * Later pages, filter changes, and sort changes stay false.
 */
export function claimListingSearchLog(page: number): boolean {
  if (!shouldAttachListingSearchLog(page, listingSearchLogArmed)) return false
  listingSearchLogArmed = false
  return true
}

/** Emit at most one event for the pending search, using the first results total. */
export function consumeSearchPerformed(input: {
  ready: boolean
  cityId?: number | null
  areaId?: number | null
  categoryId?: number | null
  intent?: string
  bhk?: string
  budgetMin?: number
  budgetMax?: number
  resultCount: number
}): boolean {
  if (!pendingSearchSource || !input.ready) return false
  const source = pendingSearchSource
  pendingSearchSource = null
  trackEvent(
    'search_performed',
    searchPerformedParams({
      cityId: input.cityId,
      areaId: input.areaId,
      categoryId: input.categoryId,
      intent: input.intent,
      bhk: input.bhk,
      budgetMin: input.budgetMin,
      budgetMax: input.budgetMax,
      resultCount: input.resultCount,
      source,
    }),
  )
  return true
}

export function trackFilterApplied(
  filters: Pick<PropertyFilters, 'intent' | 'bhk' | 'budgetMin' | 'budgetMax'>,
  cityId: number | null | undefined,
  source: string,
  ids?: { areaId?: number; categoryId?: number },
): void {
  trackEvent(
    'filter_applied',
    filterAppliedParams({
      cityId,
      areaId: ids?.areaId,
      categoryId: ids?.categoryId,
      intent: filters.intent,
      bhk: filters.bhk?.length === 1 ? filters.bhk[0] : undefined,
      budgetMin: filters.budgetMin,
      budgetMax: filters.budgetMax,
      source,
    }),
  )
}

export function trackSortApplied(
  sort: string,
  currentSort: string | undefined,
  cityId: number | null | undefined,
  source: string,
): void {
  const current = currentSort || 'relevance'
  if (sort === current) return
  trackEvent('sort_applied', sortAppliedParams({ sort, cityId, source }))
}

export function trackMapSearch(cityId: number | null | undefined, resultCount: number): void {
  trackEvent('map_search', mapSearchParams({ cityId, resultCount }))
}

export function optionIdsForFilters(
  filters: Pick<PropertyFilters, 'area' | 'category'>,
  areas: readonly NamedOptionId[],
  categories: readonly NamedOptionId[],
): { areaId?: number; categoryId?: number } {
  return {
    areaId: resolveSingleOptionId(filters.area, areas),
    categoryId: resolveSingleOptionId(filters.category, categories),
  }
}
